import {
  ApiError,
  Individ,
  Jaktdag,
  NO_CONNECTION_STATUS_CODE,
  NOT_AUTHENTICATED_STATUS_CODES,
} from "~/src/api/types";
import { jaktdataApi } from "~/src/appGlobals";
import { isDeletedOnServer, syncStatusForError } from "~/src/contexts/AppDataContext/syncHelpers";
import { toErrorRecords } from "~/src/helpers/formErrors";
import { AppStorage, StorageItem } from "../StorageContext/StorageProvider";

export const syncJaktdager = async (storage: AppStorage) => {
  const pendingJaktdager = storage
    .getAll<Jaktdag>("Jaktdag")
    .filter((i) => i.syncStatus !== "unconfirmed");
  if (pendingJaktdager.length === 0) return;

  const toCreate = pendingJaktdager.filter((jaktdag) => jaktdag.operation === "create");
  const toUpdate = pendingJaktdager.filter((jaktdag) => jaktdag.operation === "update");
  const toDelete = pendingJaktdager.filter((jaktdag) => jaktdag.operation === "delete");

  let jaktdagerSynced = 0;
  let jaktdagerNotSynced = 0;
  let createRequests: Promise<void>[] = [];
  let updateRequests: Promise<void>[] = [];
  let deleteRequests: Promise<void>[] = [];

  const syncConfirm = (jaktdag: StorageItem<Jaktdag>) => {
    jaktdagerSynced += 1;
    storage.add({
      ...jaktdag,
      syncStatus: "unconfirmed",
      errors: undefined,
      isPending: false,
      lastUpdated: new Date(),
    });

    jaktdag.data.Individer.forEach((i) => {
      if (!storage.get<Individ>({ Id: i.Id }, "Individ")) {
        storage.add({
          objectKey: { Id: i.Id },
          data: i,
          syncStatus: "unconfirmed",
          isPending: false,
          lastUpdated: new Date(),
        });
      }
    });
  };

  const syncReject = (jaktdag: StorageItem<Jaktdag>, e: ApiError) => {
    if (!NOT_AUTHENTICATED_STATUS_CODES.includes(e.statusCode)) {
      if (e.message.includes("Id må være unik")) {
        //duplicates
        storage.add({
          ...jaktdag,
          operation: "create",
          syncStatus: "unconfirmed",
          errors: undefined,
          isPending: false,
        });
      } else if (e.message.includes("Jaktdag med Id må finnes i databasen")) {
        //trying to delete something that does not exist
        storage.delete(jaktdag.objectKey, "Jaktdag");
      } else if (e.statusCode === NO_CONNECTION_STATUS_CODE) {
        //timeout error
        storage.add({
          ...jaktdag,
          syncStatus: "pending",
          errors: undefined,
        });
      } else {
        jaktdagerNotSynced += 1;
        storage.add({
          ...jaktdag,
          syncStatus: syncStatusForError(e),
          errors: toErrorRecords(e),
        });
      }
    }
  };

  for (const jaktdag of toCreate) {
    const individer = storage
      .getAll<Individ>()
      .filter((i) => i.data.JaktdagId === jaktdag.data.Id && i.operation != "delete")
      .map((i) => {
        if (jaktdag.data.Art === "Hjort" && i.data.Termisk === undefined) {
          return { ...i.data, Termisk: null };
        }
        return i.data;
      });
    createRequests.push(
      jaktdataApi
        .postJaktdag({ ...jaktdag.data, Individer: individer })
        .then(() => {
          syncConfirm(jaktdag);
        })
        .catch((e: ApiError) => syncReject(jaktdag, e)),
    );
  }

  for (const jaktdag of toUpdate) {
    updateRequests.push(
      jaktdataApi
        .putJaktdagWithoutIndivid(jaktdag.data)
        .then(() => syncConfirm(jaktdag))
        .catch((e: ApiError) => {
          if (isDeletedOnServer(e)) {
            syncConfirm(jaktdag);
          } else {
            syncReject(jaktdag, e);
          }
        }),
    );
  }

  for (const jaktdag of toDelete) {
    deleteRequests.push(
      jaktdataApi
        .deleteJaktdag(jaktdag.data.Id)
        .then(() => syncConfirm(jaktdag))
        .catch((e: ApiError) => {
          if (isDeletedOnServer(e)) {
            syncConfirm(jaktdag);
          } else {
            syncReject(jaktdag, e);
          }
        }),
    );
  }

  return Promise.allSettled(deleteRequests)
    .then(() => {
      return Promise.allSettled(updateRequests);
    })
    .then(() => {
      return Promise.allSettled(createRequests);
    })
    .then(() => {
      return {
        jaktdagerSynced,
        jaktdagerNotSynced,
      };
    });
};

export type SyncJaktdagerResult = {
  jaktdagerSynced: number;
  jaktdagerNotSynced: number;
};

export const SyncJaktdagerResultDefaults: SyncJaktdagerResult = {
  jaktdagerSynced: 0,
  jaktdagerNotSynced: 0,
};
